import { defineStore, storeToRefs } from "pinia"
import { ref, computed, inject, toRaw } from "vue"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { SOURCES_TYPES } from "@core/constants/source"
import type { Services } from "@services"
import type { DimensionnementPvAnalysis } from "../types/dimensionnement-pv.types"
import type { ExtrapolationHorairePvAnalysis } from "../types/extrapolation-pv.types"
import type {
    LastEtudeEcoByAnalyseResponse,
    LastEtudePvBySourceResponse,
    SourcePvDataResponse,
    ValorisationEcoHistoryItem,
} from "../services/pv.service"

/**
 * Copie figée de l’historique dimensionnement / éco et des « dernières études »,
 * pour rollback après DELETE optimiste.
 */
export type PvListsAndLastStudiesSnapshot = {
    historyEtudePv: DimensionnementPvAnalysis[] | null
    historyEtudeEco: ValorisationEcoHistoryItem[] | null
    lastEtudePv: LastEtudePvBySourceResponse | null
    lastEtudeEco: LastEtudeEcoByAnalyseResponse | null
    lastEtudeEcoDimName: string | null
}

/**
 * Copie profonde des données API / store sans `structuredClone` : les objets
 * réactifs Vue (Proxy) provoquent un {@link DataCloneError} avec `structuredClone`.
 */
function clonePlainForPvSnapshot<T>(value: T): T {
    return JSON.parse(JSON.stringify(toRaw(value))) as T
}

export type PvRoofSegment = {
    id: string
    peakpower: number | null
    pvtechchoice: string
    loss: number | null
    optimalangles: number
    angle: number
    aspect: number
    area: number | null
    location: [number, number] | null
    selected?: boolean
    index: number
}

/** Identifiants pour les lectures PV liées à un bâtiment + une source PDL */
export type PvBootstrapIds = {
    sourceId: string
    buildingId: string
}

type FetchSourcePvDataKwargs = {
    sourceId?: string
    /**
     * Ne pas remonter l’erreur au wrapper : état `sourcePvData` remis à `null` en cas d’échec
     * (ex. chargement orchestré hors changement manuel de compteur).
     */
    swallowError?: boolean
}

type SetSourceIdOptions = {
    /** Par défaut `true` : relance `fetchSourcePvData` quand `id` est non nul */
    fetchSourcePv?: boolean
}

/**
 * Extrait l’id d’analyse d’extrapolation issu de `GET …/data/source/:id/pv` (champ
 * {@link SourcePvDataResponse#_id_extrapolation}).
 */
function normalizeExtrapolationAnalyseId(raw: unknown): string | null {
    if (raw == null || raw === "") return null
    if (typeof raw === "string" && raw.length > 0) return raw
    return null
}

/**
 * Store Pinia du parcours PV bâtiment : source PDL, données source, études et historiques.
 * Chaque appel HTTP passe par un {@link useFetchWrapper} (`fetch…`, `isFetching…`, `hasError…`, `error…`).
 * L’historique de dimensionnement sous route bâtiment est orchestré par `PageBuildingPVWrapper` ;
 * le `GET …/last` est déclenché uniquement par `PageBuildingPVDimensionnement`. Le shell gère dialogues et rafraîchissements ciblés.
 */
export const usePvStore = defineStore("pv", () => {
    // =========================================
    // Services
    // =========================================
    const services = inject<Services>("services")
    if (!services) {
        throw new Error("usePvStore: services non injectés")
    }

    const {
        $getLastEtudePvBySource,
        $getAnalysePvBySource,
        $getSourcePvData,
        $getEtudeEcoHistoryBySource,
        $getAnalyseEcoById,
        $getExtrapolationByAnalyseId,
        $getLastEtudeEcoPvByAnalysePV,
    } = services

    const buildingStore = useBuildingStore()
    const { getMeters } = storeToRefs(buildingStore)

    // =========================================
    // État (refs)
    // =========================================
    const sourceId = ref<string | null>(null)

    const extrapolation = ref<ExtrapolationHorairePvAnalysis | null>(null)

    const historyEtudePv = ref<DimensionnementPvAnalysis[] | null>(null)

    const historyEtudeEco = ref<ValorisationEcoHistoryItem[] | null>(null)

    const lastEtudePv = ref<LastEtudePvBySourceResponse | null>(null)

    const lastEtudeEco = ref<LastEtudeEcoByAnalyseResponse | null>(null)

    /**
     * Nom d’affichage du dimensionnement lié (ligne d’en-tête PV) — renseigné
     * lors d’un chargement d’étude éco par source.
     */
    const lastEtudeEcoDimName = ref<string | null>(null)

    const sourcePvData = ref<SourcePvDataResponse | null>(null)

    /**
     * Dernière référence d’analyse d’extrapolation prête pour le formulaire / le shell,
     * dérivée du `GET …/data/source/…/pv` (stable même si l’objet `extrapolation` n’est
     * pas chargé en détail).
     */
    const lastExtrapolationAnalyseId = ref<string | null>(null)

    /**
     * Compteur auquel appartiennent `sourcePvData` et `lastExtrapolationAnalyseId`
     * (DEV-2196) : une réponse d'un autre compteur ne sert jamais le compteur courant.
     */
    const sourcePvDataSourceId = ref<string | null>(null)

    /**
     * Bâtiment auquel appartient l'état PV (DEV-2196) : tenu par
     * {@link ensureBuildingScope}, y compris quand la page PV est remontée.
     */
    const scopedBuildingId = ref<string | null>(null)

    /**
     * Liste locale des segments de toiture en cours d'édition.
     */
    const roofSegments = ref<PvRoofSegment[]>([])

    /**
     * Dernière étude de dimensionnement soumise depuis le formulaire PV.
     */
    const lastPvFormPayload = ref<Record<string, unknown> | null>(null)

    /**
     * Dernière étude économique soumise depuis le formulaire PV.
     */
    const lastEtudeEcoPayload = ref<Record<string, unknown> | null>(null)

    // =========================================
    // Getters (computed)
    // =========================================
    const getSourceId = computed(() => sourceId.value)

    /**
     * Id d'extrapolation du compteur COURANT, lu seulement dans la réponse
     * `GET …/data/source/:id/pv` de ce compteur. Jamais de repli sur
     * `extrapolation` en mémoire : elle peut être celle d'un autre compteur
     * (DEV-2196, export lancé avec l'extrapolation d'un autre bâtiment).
     */
    const getExtrapolationId = computed(() => {
        if (!sourceId.value || sourcePvDataSourceId.value !== sourceId.value) {
            return null
        }
        return (
            lastExtrapolationAnalyseId.value ??
            normalizeExtrapolationAnalyseId(
                sourcePvData.value?._id_extrapolation
            ) ??
            null
        )
    })

    const getLastEtudePv = computed(() => lastEtudePv.value)

    const getLastEtudeEco = computed(() => lastEtudeEco.value)

    const getLastEtudeEcoDimName = computed(() => lastEtudeEcoDimName.value)

    const getEtudePvHistory = computed(() => historyEtudePv.value)

    const getEtudeEcoHistory = computed(() => historyEtudeEco.value)

    // =========================================
    // Fetch wrappers — un useFetchWrapper par appel service
    // =========================================
    const {
        fetch: fetchBootstrapBuildingSources,
        isFetching: isFetchingBootstrapBuildingSources,
        hasError: hasErrorBootstrapBuildingSources,
        error: errorBootstrapBuildingSources,
    } = useFetchWrapper(async () => {
        await buildingStore.fetchBuildingSources({
            sourceTypes: [SOURCES_TYPES.PDL],
        })
    })

    const {
        fetch: fetchLastEtudePvBySource,
        isFetching: isFetchingLastEtudePvBySource,
        hasError: hasErrorLastEtudePvBySource,
        error: errorLastEtudePvBySource,
    } = useFetchWrapper(
        async (ids: PvBootstrapIds | undefined) => {
            if (!ids) {
                lastEtudePv.value = null
                return
            }
            try {
                lastEtudePv.value = await $getLastEtudePvBySource(
                    ids.sourceId,
                    ids.buildingId
                )
            } catch {
                lastEtudePv.value = null
            }
        },
        () => {
            lastEtudePv.value = null
        }
    )

    const {
        fetch: fetchDimensionnementHistoryBySource,
        isFetching: isFetchingDimensionnementHistoryBySource,
        hasError: hasErrorDimensionnementHistoryBySource,
        error: errorDimensionnementHistoryBySource,
    } = useFetchWrapper(
        async (ids: PvBootstrapIds | undefined) => {
            if (!ids) {
                historyEtudePv.value = null
                return
            }
            try {
                const hist = await $getAnalysePvBySource(
                    ids.sourceId,
                    ids.buildingId
                )
                historyEtudePv.value = hist.analyses
            } catch {
                historyEtudePv.value = []
            }
        },
        () => {
            historyEtudePv.value = null
        }
    )

    const {
        fetch: fetchValorisationHistoryBySource,
        isFetching: isFetchingValorisationHistoryBySource,
        hasError: hasErrorValorisationHistoryBySource,
        error: errorValorisationHistoryBySource,
    } = useFetchWrapper(
        async (ids: PvBootstrapIds | undefined) => {
            if (!ids) {
                historyEtudeEco.value = null
                return
            }
            try {
                const hist = await $getEtudeEcoHistoryBySource(
                    ids.sourceId,
                    ids.buildingId
                )
                historyEtudeEco.value = hist.analyses
            } catch {
                historyEtudeEco.value = []
            }
        },
        () => {
            historyEtudeEco.value = null
        }
    )

    const {
        fetch: fetchExtrapolationByAnalyseId,
        isFetching: isFetchingExtrapolationByAnalyseId,
        hasError: hasErrorExtrapolationByAnalyseId,
        error: errorExtrapolationByAnalyseId,
    } = useFetchWrapper(
        async (analyseId: string | null | undefined) => {
            if (!analyseId) {
                extrapolation.value = null
                return
            }
            try {
                extrapolation.value =
                    await $getExtrapolationByAnalyseId(analyseId)
            } catch {
                extrapolation.value = null
            }
        },
        () => {
            extrapolation.value = null
        }
    )

    const {
        fetch: fetchLastValorisationByDimensionnementId,
        isFetching: isFetchingLastValorisationByDimensionnementId,
        hasError: hasErrorLastValorisationByDimensionnementId,
        error: errorLastValorisationByDimensionnementId,
    } = useFetchWrapper(
        async (dimensionnementId: string | null | undefined) => {
            if (!dimensionnementId) {
                lastEtudeEco.value = null
                return
            }
            try {
                lastEtudeEco.value =
                    await $getLastEtudeEcoPvByAnalysePV(dimensionnementId)
            } catch {
                lastEtudeEco.value = null
            }
        },
        () => {
            lastEtudeEco.value = null
        }
    )

    /**
     * Dernière étude économique **réussie** pour une source (historique le plus
     * récent), puis détail `GET …/etude_eco/:id` (graphes, template).
     */
    const {
        fetch: fetchLastEtudeEcoForSource,
        isFetching: isFetchingLastEtudeEcoForSource,
        hasError: hasErrorLastEtudeEcoForSource,
        error: errorLastEtudeEcoForSource,
    } = useFetchWrapper(
        async (ids: PvBootstrapIds | undefined) => {
            lastEtudeEcoDimName.value = null
            if (!ids) {
                lastEtudeEco.value = null
                return
            }
            try {
                const hist = await $getEtudeEcoHistoryBySource(
                    ids.sourceId,
                    ids.buildingId
                )
                const rows = hist.analyses ?? []
                if (rows.length === 0) {
                    lastEtudeEco.value = null
                    return
                }
                const sorted = [...rows].sort((a, b) => {
                    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
                    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
                    return tb - ta
                })
                const latest = sorted[0]
                const dimLabel = latest.name_dimensionnement
                lastEtudeEcoDimName.value =
                    typeof dimLabel === "string" && dimLabel.length > 0
                        ? dimLabel
                        : null
                const detail = await $getAnalyseEcoById(latest._id)
                const mergedAnalyse: LastEtudeEcoByAnalyseResponse["lastAnalyse"] =
                    detail.analyse
                        ? {
                              ...detail.analyse,
                              template:
                                  detail.analyse.template ??
                                  detail.template ??
                                  undefined,
                          }
                        : null
                lastEtudeEco.value = {
                    template: detail.template ?? null,
                    lastAnalyse: mergedAnalyse,
                }
            } catch {
                lastEtudeEco.value = null
                lastEtudeEcoDimName.value = null
            }
        },
        () => {
            lastEtudeEco.value = null
            lastEtudeEcoDimName.value = null
        }
    )

    const {
        fetch: fetchSourcePvData,
        isFetching: isFetchingSourcePvData,
        hasError: hasErrorSourcePvData,
        error: errorSourcePvData,
        cancel: cancelSourcePvDataFetch,
    } = useFetchWrapper(
        async (kwargs?: FetchSourcePvDataKwargs) => {
            const sid = kwargs?.sourceId ?? sourceId.value
            const swallow = kwargs?.swallowError === true
            if (!sid) {
                sourcePvData.value = null
                lastExtrapolationAnalyseId.value = null
                sourcePvDataSourceId.value = null
                return
            }
            try {
                const data = await $getSourcePvData(sid)
                sourcePvData.value = data
                lastExtrapolationAnalyseId.value =
                    normalizeExtrapolationAnalyseId(data?._id_extrapolation)
                sourcePvDataSourceId.value = sid
            } catch (e) {
                if (swallow) {
                    sourcePvData.value = null
                    lastExtrapolationAnalyseId.value = null
                    sourcePvDataSourceId.value = null
                    return
                }
                throw e
            }
        },
        () => {
            sourcePvData.value = null
            lastExtrapolationAnalyseId.value = null
            sourcePvDataSourceId.value = null
        }
    )

    // =========================================
    // Getters agrégés (bandeau shell)
    // =========================================
    const isPvContextFetching = computed(
        () =>
            isFetchingBootstrapBuildingSources.value ||
            isFetchingLastEtudePvBySource.value ||
            isFetchingDimensionnementHistoryBySource.value ||
            isFetchingValorisationHistoryBySource.value ||
            isFetchingExtrapolationByAnalyseId.value ||
            isFetchingLastValorisationByDimensionnementId.value ||
            isFetchingSourcePvData.value
    )

    const hasPvContextError = computed(
        () =>
            hasErrorBootstrapBuildingSources.value ||
            hasErrorLastEtudePvBySource.value ||
            hasErrorDimensionnementHistoryBySource.value ||
            hasErrorValorisationHistoryBySource.value ||
            hasErrorExtrapolationByAnalyseId.value ||
            hasErrorLastValorisationByDimensionnementId.value
    )

    const pvContextError = computed(
        () =>
            errorBootstrapBuildingSources.value ??
            errorLastEtudePvBySource.value ??
            errorDimensionnementHistoryBySource.value ??
            errorValorisationHistoryBySource.value ??
            errorExtrapolationByAnalyseId.value ??
            errorLastValorisationByDimensionnementId.value ??
            null
    )

    // =========================================
    // Méthodes
    // =========================================
    function getDefaultPdlSourceId(): string | null {
        const pdls = getMeters.value.filter((s) => s.type === SOURCES_TYPES.PDL)
        return pdls[0]?._id_source ?? null
    }

    /**
     * Retourne `sourceId` si c'est un PDL du bâtiment courant, sinon retombe
     * sur {@link getDefaultPdlSourceId}. Évite la propagation d'un `sourceId`
     * hérité d'un autre bâtiment après navigation SPA (DEV-378).
     */
    function getResolvedSourceIdForCurrentBuilding(): string | null {
        const stored = sourceId.value
        if (stored) {
            const inCurrent = getMeters.value.some(
                (s) => s._id_source === stored && s.type === SOURCES_TYPES.PDL
            )
            if (inCurrent) return stored
        }
        return getDefaultPdlSourceId()
    }

    /**
     * Vide tout le state PV building-scoped. Appelé au changement de bâtiment
     * (cf. {@link PageBuildingPVWrapper}) pour ne pas laisser de données du
     * bâtiment précédent dans l'UI ou les payloads d'export (DEV-378).
     */
    function resetForBuildingChange(): void {
        cancelSourcePvDataFetch()
        sourceId.value = null
        sourcePvData.value = null
        lastExtrapolationAnalyseId.value = null
        sourcePvDataSourceId.value = null
        extrapolation.value = null
        lastEtudePv.value = null
        lastEtudeEco.value = null
        lastEtudeEcoDimName.value = null
        historyEtudePv.value = null
        historyEtudeEco.value = null
        lastPvFormPayload.value = null
        lastEtudeEcoPayload.value = null
    }

    /**
     * Tient l'état PV dans le périmètre du bâtiment affiché (DEV-2196). Appelé au
     * montage de {@link PageBuildingPVWrapper} ET à chaque changement de bâtiment :
     * un autre bâtiment vide l'état, même si la page PV a été quittée entre-temps
     * (le store survit au démontage) ; le même bâtiment le garde.
     */
    function ensureBuildingScope(buildingId: string | null): void {
        if (!buildingId || scopedBuildingId.value === buildingId) return
        if (scopedBuildingId.value !== null) resetForBuildingChange()
        scopedBuildingId.value = buildingId
    }

    function resetPvDerivedContext(): void {
        lastEtudePv.value = null
        historyEtudePv.value = null
        extrapolation.value = null
        lastEtudeEco.value = null
        lastEtudeEcoDimName.value = null
        historyEtudeEco.value = null
    }

    function setSourceId(
        id: string | null,
        options?: SetSourceIdOptions
    ): void {
        const shouldFetchPv = options?.fetchSourcePv !== false
        sourceId.value = id
        if (!id) {
            sourcePvData.value = null
            lastExtrapolationAnalyseId.value = null
            cancelSourcePvDataFetch()
            return
        }
        if (shouldFetchPv) {
            void fetchSourcePvData()
        }
    }

    function unsetExtrapolation(): void {
        extrapolation.value = null
    }

    /**
     * Capture l’état listes + dernières études avant une suppression optimiste
     * (DELETE dimensionnement ou étude éco).
     */
    function snapshotPvListsAndLastStudies(): PvListsAndLastStudiesSnapshot {
        return {
            historyEtudePv:
                historyEtudePv.value == null
                    ? null
                    : clonePlainForPvSnapshot(historyEtudePv.value),
            historyEtudeEco:
                historyEtudeEco.value == null
                    ? null
                    : clonePlainForPvSnapshot(historyEtudeEco.value),
            lastEtudePv:
                lastEtudePv.value == null
                    ? null
                    : clonePlainForPvSnapshot(lastEtudePv.value),
            lastEtudeEco:
                lastEtudeEco.value == null
                    ? null
                    : clonePlainForPvSnapshot(lastEtudeEco.value),
            lastEtudeEcoDimName: lastEtudeEcoDimName.value,
        }
    }

    /**
     * Restaure l’état capturé par {@link snapshotPvListsAndLastStudies} après échec API.
     */
    function restorePvListsAndLastStudies(
        snapshot: PvListsAndLastStudiesSnapshot
    ): void {
        historyEtudePv.value =
            snapshot.historyEtudePv == null
                ? null
                : clonePlainForPvSnapshot(snapshot.historyEtudePv)
        historyEtudeEco.value =
            snapshot.historyEtudeEco == null
                ? null
                : clonePlainForPvSnapshot(snapshot.historyEtudeEco)
        lastEtudePv.value =
            snapshot.lastEtudePv == null
                ? null
                : clonePlainForPvSnapshot(snapshot.lastEtudePv)
        lastEtudeEco.value =
            snapshot.lastEtudeEco == null
                ? null
                : clonePlainForPvSnapshot(snapshot.lastEtudeEco)
        lastEtudeEcoDimName.value = snapshot.lastEtudeEcoDimName
    }

    function removeAnalyseFromHistory(analyseId: string): void {
        if (historyEtudePv.value) {
            historyEtudePv.value = historyEtudePv.value.filter(
                (a) => a._id !== analyseId
            )
        }
        if (historyEtudeEco.value) {
            historyEtudeEco.value = historyEtudeEco.value.filter(
                (a) => a._id_dimensionnement !== analyseId
            )
        }

        const pvLast = lastEtudePv.value
        if (pvLast?.lastAnalyse?._id === analyseId) {
            const rows = historyEtudePv.value
            const nextLast =
                rows && rows.length > 0
                    ? [...rows].sort((a, b) => {
                          const ta = a.createdAt
                              ? new Date(a.createdAt).getTime()
                              : 0
                          const tb = b.createdAt
                              ? new Date(b.createdAt).getTime()
                              : 0
                          return tb - ta
                      })[0]
                    : null
            lastEtudePv.value = {
                template: pvLast.template,
                lastAnalyse: nextLast,
            }
        }

        const eco = lastEtudeEco.value?.lastAnalyse
        const dimFromEco = eco?.inputs?._id_dimensionnement
        if (typeof dimFromEco === "string" && dimFromEco === analyseId) {
            lastEtudeEco.value = null
            lastEtudeEcoDimName.value = null
        }
    }

    function removeAnalyseEcoFromHistory(analyseId: string): void {
        if (historyEtudeEco.value) {
            historyEtudeEco.value = historyEtudeEco.value.filter(
                (a) => a._id !== analyseId
            )
        }
        if (lastEtudeEco.value?.lastAnalyse?._id === analyseId) {
            lastEtudeEco.value = null
            lastEtudeEcoDimName.value = null
        }
    }

    /**
     * Remplace l’état « dernière étude éco » (ex. masquage optimiste avant DELETE,
     * restauration après erreur).
     */
    function replaceLastEtudeEco(
        payload: LastEtudeEcoByAnalyseResponse | null
    ): void {
        lastEtudeEco.value = payload
    }

    function setRoofSegments(segments: PvRoofSegment[]): void {
        roofSegments.value = segments
    }

    function setLastPvFormPayload(payload: Record<string, unknown>): void {
        lastPvFormPayload.value = payload
    }

    function setLastEtudeEcoPayload(payload: Record<string, unknown>): void {
        lastEtudeEcoPayload.value = payload
    }

    return {
        sourceId,
        extrapolation,
        historyEtudePv,
        historyEtudeEco,
        lastEtudePv,
        lastEtudeEco,
        lastEtudeEcoDimName,
        sourcePvData,
        roofSegments,
        lastPvFormPayload,
        lastEtudeEcoPayload,
        lastExtrapolationAnalyseId,

        getSourceId,
        getExtrapolationId,
        getLastEtudePv,
        getLastEtudeEco,
        getLastEtudeEcoDimName,
        getEtudePvHistory,
        getEtudeEcoHistory,

        getDefaultPdlSourceId,
        getResolvedSourceIdForCurrentBuilding,
        resetForBuildingChange,
        ensureBuildingScope,
        resetPvDerivedContext,
        setSourceId,
        unsetExtrapolation,
        snapshotPvListsAndLastStudies,
        restorePvListsAndLastStudies,
        removeAnalyseFromHistory,
        removeAnalyseEcoFromHistory,
        replaceLastEtudeEco,
        setRoofSegments,
        setLastPvFormPayload,
        setLastEtudeEcoPayload,

        isPvContextFetching,
        hasPvContextError,
        pvContextError,

        fetchBootstrapBuildingSources,
        isFetchingBootstrapBuildingSources,
        hasErrorBootstrapBuildingSources,
        errorBootstrapBuildingSources,

        fetchLastEtudePvBySource,
        isFetchingLastEtudePvBySource,
        hasErrorLastEtudePvBySource,
        errorLastEtudePvBySource,

        fetchDimensionnementHistoryBySource,
        isFetchingDimensionnementHistoryBySource,
        hasErrorDimensionnementHistoryBySource,
        errorDimensionnementHistoryBySource,

        fetchValorisationHistoryBySource,
        isFetchingValorisationHistoryBySource,
        hasErrorValorisationHistoryBySource,
        errorValorisationHistoryBySource,

        fetchExtrapolationByAnalyseId,
        isFetchingExtrapolationByAnalyseId,
        hasErrorExtrapolationByAnalyseId,
        errorExtrapolationByAnalyseId,

        fetchLastValorisationByDimensionnementId,
        isFetchingLastValorisationByDimensionnementId,
        hasErrorLastValorisationByDimensionnementId,
        errorLastValorisationByDimensionnementId,

        fetchLastEtudeEcoForSource,
        isFetchingLastEtudeEcoForSource,
        hasErrorLastEtudeEcoForSource,
        errorLastEtudeEcoForSource,

        fetchSourcePvData,
        isFetchingSourcePvData,
        hasErrorSourcePvData,
        errorSourcePvData,
        cancelSourcePvDataFetch,
    }
})
